import sortBy from "lodash/sortBy";
import moment from "moment";
import React, { useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";

import Box from "veneer/Box";
import Stack from "veneer/Stack";
import styled from "veneer/styled";
import { Colors } from "veneer/Theme";
import Tooltip from "veneer/Tooltip";

import { UptimeReport } from "../model";

interface UptimeChartProps {
  data: Record<string, number>;
}

export default function UptimeChart({ data }: UptimeChartProps) {
  if (!data || Object.keys(data).length === 0) {
    return null;
  }
  const { networkId, start, end } = useParams<{
    networkId?: string;
    start?: string;
    end?: string;
  }>();
  const history = useHistory();
  const points: ReadonlyArray<UptimeReport> = useMemo(() => {
    const arr: ReadonlyArray<UptimeReport> = Object.entries(data).map(
      ([date, uptime]) => ({
        date: moment(date),
        uptime,
      }),
    );
    return sortBy(arr, "date");
  }, [data]);

  if (!points.length) {
    return null;
  }

  const width = 100 / points.length;

  return (
    <Stack dataTestId="uptime-chart" direction="row" spacing={0.5} width="100%">
      {points.map((row, idx) => {
        const date = row.date;
        const title = `${date.format("MM/DD/YYYY")}: ${Number(
          row.uptime,
        ).toFixed(2)}%`;

        const url =
          `/diagnostics/uptime-report/${networkId}/` +
          `${start}/${end}/devices/` +
          `${date.format("YYYY-MM-DD")}`;

        return (
          <Box
            dataTestId="uptime-chart-box"
            height={20}
            key={idx}
            width={`${width}%`}
          >
            <Tooltip title={title}>
              <PointerBox
                bgcolor={getColor(row.uptime)}
                height="100%"
                onClick={() => history.push(url)}
                width="100%"
              />
            </Tooltip>
          </Box>
        );
      })}
    </Stack>
  );
}

function getColor(uptime: number): string {
  if (uptime > 95) {
    return Colors.green600;
  } else if (uptime > 60) {
    return Colors.yellow600;
  }
  return Colors.red600;
}

const PointerBox = styled(Box)({
  cursor: "pointer",
});
